import { observable, action, computed, runInAction } from 'mobx';
import { models, query, utils } from '@kurtosys/ksys-app-template';
import { QueryContext } from '@kurtosys/ksys-api-client/dist/query/QueryContext';
import { QueryStore as QueryStoreBase } from '@kurtosys/ksys-app-components/dist/shared/QueryStore';
import { IQueryContextClient } from '@kurtosys/ksys-api-client/dist/models/query/IQueryContextClient';
import { StoreContext } from '../../../configuration/StoreContext';
import { IEntitiesRequestOptions } from '../../../models/app/IEntitiesRequestOptions';
import { IQueryOptions, IExecutionOptions, IQueryContext, IQueryContextCulture, IBatchPromiseElement, IBatchOptions, IFundManager, IStatistics, IQueryContextConfiguration } from '../../../models/commonTypes';
import { IConfigurationComponentOrchestration } from '../../../models/app/IConfigurationComponentOrchestration';
import { IRequestTypeOptionsExternalApi } from '../../../models/IRequestTypeOptionsExternalApi';
import { IRequestTypeOptionsExternalApiRequest } from '../../../models/IRequestTypeOptionsExternalApiRequest';
const Query = query.Query;
export class QueryStore extends QueryStoreBase {
	storeContext: StoreContext;
	@observable.ref context: IQueryContext | undefined = undefined;
	@observable.ref fundManagerContext: IFundManager | undefined = undefined;
	@observable.ref responseContext: object | undefined = undefined;
	@observable.ref statisticContext: IStatistics | undefined = undefined;
	@observable.ref private updatedInputs: Record<string, any> = {};

	constructor(storeContext: StoreContext) {
		super();
		this.storeContext = storeContext;
	}

	@computed
	get client(): IQueryContextClient {
		const { kurtosysApiStore } = this.storeContext;
		return kurtosysApiStore.client;
	}

	@computed
	get inputs(): Record<string, any> {
		const { appStore } = this.storeContext;
		const { appParamsHelper } = appStore;
		return { ...appParamsHelper.inputs, ...this.updatedInputs };
	}

	@computed
	get executionOptions(): IExecutionOptions {
		const { appStore, translationStore } = this.storeContext;
		return {
			applicationGuid: appStore.applicationGuid,
			defaultContextParentKey: this.defaultContextParentKey,
			context: this.context,
			responseContext: this.responseContext,
			fundManagerContext: this.fundManagerContext,
			statisticContext: this.statisticContext,
			culture: appStore.getInput('culture'),
			fallbackCulture:
				appStore.configuration && appStore.configuration.culture && appStore.configuration.culture.default,
			translate: translationStore.translate,
			inputs: this.inputs,
			serviceUrl: appStore.serviceUrl,
			globalFormatsByKey: appStore.globalFormatsByKey,
		};
	}
	get defaultContextParentKey(): string {
		const { appStore } = this.storeContext;
		const { configuration, storeKey } = appStore;
		let response: string = storeKey;
		if (configuration) {
			const { data } = configuration;
			if (data) {
				const { key } = data;
				if (key) {
					response = key;
				}
			}
		}
		return response;
	}

	/**
	 * Returns the target context & contextConfiguration
	 *
	 * @param [targetContext={ contextParentKey: this.defaultContextParentKey, contextKey: 'default' }]
	 */
	getContext(
		targetContext: models.context.IGetContextOptions = { contextParentKey: this.defaultContextParentKey, contextKey: 'default' },
	): models.context.IContextAndConfiguration {
		return query.Query.contextStore.get(targetContext) ?? {};
	}

	/**
	 * Set the target context & contextConfiguration with a new value
	 *
	 * @param targetContext
	 */
	setContext(targetContext: models.context.ISetContextOptions) {
		const { contextParentKey = this.defaultContextParentKey, contextKey = 'default', value } = targetContext;
		query.Query.contextStore.set({
			contextParentKey,
			contextKey,
			value,
		});
	}

	@action async initialize() {
		const { inputStore } = this.storeContext;
		const globalInputs = await inputStore.getAllFromStorage();
		this.updateInputs(globalInputs, true);
	}

	/**
	 * Updates the input overrides for the query contexts and loads the contexts with the new inputs.
	 *
	 * @param {Record<string, any>} [inputOverrides] - Optional input overrides for the query contexts.
	 * @param {boolean} [init=false] - Whether this is the initial update of the inputs.
	 * @returns {Promise<void>} - A Promise that resolves when all query contexts have been loaded with the new inputs.
	 */
	@action async updateInputs(inputOverrides?: any, init = false) {
		const { appStore } = this.storeContext;
		const { configuration } = appStore;
		const inputs = { ...this.inputs, ...inputOverrides };
		appStore.logDebug(`query.Query Store > ${ init ? 'Init' : 'Update' } Inputs`, {
			current: this.inputs,
			next: inputs,
		});
		if (configuration && configuration.data && configuration.data.context) {
			appStore.dataContextStatus = init ? 'PENDING' : 'UPDATING';
			const contexts = Array.isArray(configuration.data.context) ? configuration.data.context : [configuration.data.context];
			await this.loadContexts(contexts, inputs);
		}
		runInAction(() => {
			this.updatedInputs = inputs;
		});
		appStore.contextsDidUpdate();
		appStore.dataContextStatus = 'READY';
	}

	@action
	async loadContexts(contexts: IQueryContextConfiguration[], inputs?: Record<string, any>) {
		const { appStore, kurtosysApiStore } = this.storeContext;
		const { configuration, dataContextSeed } = appStore;
		const batchPromises: IBatchPromiseElement[] = [];
		const queryContextCulture: IQueryContextCulture = {
			sourceCulture: (configuration && configuration.culture && configuration.culture.base) || 'en-GB',
			targetCulture: (inputs && inputs.culture) || (configuration && configuration.culture && configuration.culture.default) || 'en-GB',
		};
		let contextCounter = 0;
		for (const context of contexts) {
			const queryContext = new QueryContext(context, dataContextSeed);
			const defaultContextKey = `default${ contextCounter > 0 ? contextCounter + 1 : '' }`;
			if (!context.contextKey) {
				context.contextKey = defaultContextKey;
			}
			batchPromises.push({
				identifier: {
					contextParentKey: (configuration && configuration.data && configuration.data.key) || this.defaultContextParentKey,
					contextKey: context.contextKey,
				},
				promiseFunc: () => queryContext.fetch(kurtosysApiStore.client, inputs || {}, queryContextCulture),
				responseFunc: (response: any, identifier: any) => {
					appStore.logDebug('Query Store > new query context', response);
					query.Query.contextStore.set({
						...identifier,
						value: {
							context: response,
							contextConfiguration: context,
						},
					});
				},
			});
			contextCounter++;
		}
		const batchOptions: IBatchOptions = {
			maxBatchSize: 10,
		};
		await utils.promise.batchExecutePromises(batchPromises, batchOptions);
	}

	query(options?: IQueryOptions, overrideExecutionOptions?: Partial<IExecutionOptions>) {
		if (options) {
			const { translationStore } = this.storeContext;
			const translationHelper = translationStore && translationStore.translationHelper;
			const query = new Query(options, translationHelper);
			const workingExecutionOptions = query.mergeExecutionOptions(
				this.executionOptions,
				overrideExecutionOptions || {},
			);
			const queryResponse = query.execute(workingExecutionOptions);
			return queryResponse;
		}
	}
	async entitiesRequest(
		id: string,
		inputs: object,
		contextParentKey: string = this.defaultContextParentKey,
		contextKey: string = 'default',
		options?: IEntitiesRequestOptions,
	) {
		const contextAndConfiguration = query.Query.contextStore.get({ contextParentKey, contextKey });
		const { contextConfiguration } = contextAndConfiguration;
		if (contextConfiguration) {
			const { appStore } = this.storeContext;
			const { dataContextSeed } = appStore;
			const queryContext = new QueryContext(contextConfiguration, dataContextSeed);
			if (queryContext && contextConfiguration) {
				let fetchEntitiesOptions;
				const executionOptions = this.executionOptions;
				if (options) {
					fetchEntitiesOptions = options.fetchEntitiesOptions;
					if (options.overrideDynamicInputs === true) {
						if (!executionOptions.inputs) {
							executionOptions.inputs = {};
						}
						Object.assign(executionOptions.inputs, inputs);
					}
				}
				return await queryContext.fetchEntities(
					this.client,
					id,
					inputs,
					contextConfiguration,
					executionOptions,
					fetchEntitiesOptions,
				);
			}
		}
	}
	async loadExternalApiRequest(options: IRequestTypeOptionsExternalApi, inputs: object) {
		const { request, responseKey } = options;
		const { id, contextParentKey = this.defaultContextParentKey, contextKey = 'default' } = request;
		const contextAndConfiguration = query.Query.contextStore.get({ contextParentKey, contextKey });
		const { contextConfiguration } = contextAndConfiguration;
		if (contextConfiguration && contextConfiguration.externalApiRequests) {
			const externalApiRequest = contextConfiguration.externalApiRequests.find(
				(externalApiRequest) => externalApiRequest.id === id,
			);
			if (externalApiRequest) {
				const { kurtosysApiStore, queryStore } = this.storeContext;
				try {
					const { id, name, providerKey, body: bodyOptions, debug } = externalApiRequest;
					let body: any = {};

					if (bodyOptions) {
						let rootKey: keyof models.query.IQueryContextExternalApiRequestBody | undefined;
						if (providerKey === models.portal.ProviderKey.MPI) {
							rootKey = 'mpi';
						}
						if (providerKey === models.portal.ProviderKey.DXM) {
							rootKey = 'dxm';
						}
						if (rootKey) {
							body = bodyOptions[rootKey] && (bodyOptions[rootKey] as any)[name];
						}
					}
					if (body) {
						body = Query.convertQueryMime(body, this.executionOptions);
					}
					const fullBody = { name, providerKey, body, debug };

					const response = await kurtosysApiStore.externalApiExecute.execute({
						body: fullBody,
					});
					if (response && response.valid && response.value) {
						queryStore.loadExternalApiResponseToDefaultContext(request, externalApiRequest, response.value);
					}
				} catch (ex) {
					console.error('Failed fetching from the external api: ', options, { externalApiRequest });
					console.error(ex);
				}
			}
		}
	}
	mergeQueriesAndProps<T>(
		componentOrchestration?: IConfigurationComponentOrchestration,
		responseContext?: any,
	): T | undefined {
		if (componentOrchestration) {
			const { translationStore } = this.storeContext;
			const { props, queries } = componentOrchestration;
			const translationHelper = translationStore && translationStore.translationHelper;
			let workingExecutionOptions = this.executionOptions;
			if (responseContext) {
				workingExecutionOptions = {
					...workingExecutionOptions,
					responseContext,
				};
			}
			const queryResults = queries
				? query.Query.convertQueryMime(queries, workingExecutionOptions, { translationHelper })
				: {};
			const response: T = utils.object.deepMergeObjectsWithOptions(
				{ arrayMergeStrategy: 'DeepMerge' },
				props || {},
				queryResults,
			);
			return response;
		}
	}
	loadExternalApiResponseToDefaultContext(
		request: IRequestTypeOptionsExternalApiRequest,
		externalApiRequest: models.query.IQueryContextExternalApiRequest,
		response: any,
	) {
		const { id, contextParentKey = this.defaultContextParentKey, contextKey = 'default' } = request;
		const identifier: models.context.IGetContextOptions = {
			contextKey,
			contextParentKey,
		};
		let existing = query.Query.contextStore.get(identifier);
		if (!existing) {
			query.Query.contextStore.set({
				...identifier,
				value: {
					context: {},
					contextConfiguration: {},
				},
			});
			existing = query.Query.contextStore.get(identifier);
		}
		if (existing) {
			if (!existing.context) {
				existing.context = {};
			}
			if (!existing.context.externalApiResponses) {
				existing.context.externalApiResponses = {};
			}
			if (!existing.context.externalApiResponses[id]) {
				existing.context.externalApiResponses[id] = {};
			}
			const contextForId = existing.context.externalApiResponses[id];
			const { providerKey, name } = externalApiRequest;
			const camelProviderKey = utils.changeCase.camelCase(providerKey);
			if (!(contextForId as any)[camelProviderKey]) {
				(contextForId as any)[camelProviderKey] = {};
			}
			(contextForId as any)[camelProviderKey][name] = response;
		}
		console.log({ existing });
	}
}
